import { useEffect, useState } from 'react';
import Modal from '../ui/Modal';

export const PAYMENT_METHOD_TYPES = [
  { value: 'bank_account', label: 'Bank account (bank transfer)' },
  { value: 'cash', label: 'Cash' },
  { value: 'card', label: 'Card (in-person)' },
  { value: 'cheque', label: 'Cheque' },
  { value: 'payhere_link', label: 'PayHere checkout link' },
  { value: 'stripe_link', label: 'Stripe checkout link' },
  { value: 'paypal_link', label: 'PayPal link' },
  { value: 'wise_link', label: 'Wise link' },
  { value: 'other_link', label: 'Other payment link' },
];

const LINK_TYPES = new Set([
  'payhere_link',
  'stripe_link',
  'paypal_link',
  'wise_link',
  'other_link',
]);

const AUDIENCES = [
  { value: 'student', label: 'Students (public & in-app payments)' },
  { value: 'reception', label: 'Reception / staff (recording in-person payments)' },
];

const defaultValues = {
  type: 'bank_account',
  label: '',
  enabled: true,
  availableFor: ['student'],
  details: {},
  instructions: '',
};

const PaymentMethodForm = ({ isOpen, onClose, onSubmit, method, submitting }) => {
  const [values, setValues] = useState(defaultValues);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    if (method) {
      setValues({
        type: method.type || 'bank_account',
        label: method.label || '',
        enabled: method.enabled !== false,
        availableFor: Array.isArray(method.availableFor) && method.availableFor.length
          ? method.availableFor
          : ['student'],
        details: method.details || {},
        instructions: method.instructions || '',
      });
    } else {
      setValues(defaultValues);
    }
    setError('');
  }, [isOpen, method]);

  const updateDetail = (key, value) =>
    setValues((prev) => ({
      ...prev,
      details: { ...(prev.details || {}), [key]: value },
    }));

  const toggleAudience = (audience) =>
    setValues((prev) => {
      const current = new Set(prev.availableFor || []);
      if (current.has(audience)) current.delete(audience);
      else current.add(audience);
      return { ...prev, availableFor: Array.from(current) };
    });

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    if (!values.label.trim()) {
      setError('Provide a display label students will see.');
      return;
    }
    if (!values.availableFor.length) {
      setError('Select at least one audience.');
      return;
    }
    if (values.type === 'bank_account') {
      const { accountNumber, accountName, bankName } = values.details || {};
      if (!accountNumber || !accountName || !bankName) {
        setError('Bank accounts need bank name, account name and account number.');
        return;
      }
    }
    if (LINK_TYPES.has(values.type)) {
      const url = values.details?.checkoutUrl;
      if (!url || !/^https?:\/\//i.test(url)) {
        setError('Provide a valid https checkout URL.');
        return;
      }
    }
    try {
      await onSubmit(values);
      onClose?.();
    } catch (err) {
      setError(err?.message || 'Failed to save payment method.');
    }
  };

  const renderTypeFields = () => {
    if (values.type === 'bank_account') {
      return (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <label>
            <span className="mb-1 block text-sm font-medium text-stone-700">Bank name</span>
            <input
              type="text"
              value={values.details?.bankName || ''}
              onChange={(event) => updateDetail('bankName', event.target.value)}
              className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-pink-500 focus:ring-pink-500"
            />
          </label>
          <label>
            <span className="mb-1 block text-sm font-medium text-stone-700">Branch</span>
            <input
              type="text"
              value={values.details?.branch || ''}
              onChange={(event) => updateDetail('branch', event.target.value)}
              className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-pink-500 focus:ring-pink-500"
            />
          </label>
          <label>
            <span className="mb-1 block text-sm font-medium text-stone-700">Account name</span>
            <input
              type="text"
              value={values.details?.accountName || ''}
              onChange={(event) => updateDetail('accountName', event.target.value)}
              className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-pink-500 focus:ring-pink-500"
            />
          </label>
          <label>
            <span className="mb-1 block text-sm font-medium text-stone-700">Account number</span>
            <input
              type="text"
              value={values.details?.accountNumber || ''}
              onChange={(event) => updateDetail('accountNumber', event.target.value)}
              className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-pink-500 focus:ring-pink-500"
            />
          </label>
          <label className="sm:col-span-2">
            <span className="mb-1 block text-sm font-medium text-stone-700">SWIFT / IFSC (optional)</span>
            <input
              type="text"
              value={values.details?.swiftCode || ''}
              onChange={(event) => updateDetail('swiftCode', event.target.value)}
              className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-pink-500 focus:ring-pink-500"
            />
          </label>
        </div>
      );
    }
    if (LINK_TYPES.has(values.type)) {
      return (
        <label className="block">
          <span className="mb-1 block text-sm font-medium text-stone-700">
            Checkout URL
          </span>
          <input
            type="url"
            placeholder="https://checkout.example.com/..."
            value={values.details?.checkoutUrl || ''}
            onChange={(event) => updateDetail('checkoutUrl', event.target.value)}
            className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-pink-500 focus:ring-pink-500"
          />
          <p className="mt-1 text-xs text-stone-500">
            Students open this URL in a new tab, complete payment, then submit the
            receipt / reference back in the LMS as proof.
          </p>
        </label>
      );
    }
    return (
      <p className="text-xs text-stone-500">
        No extra fields required. The label and instructions will be shown to
        students.
      </p>
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <form onSubmit={handleSubmit} className="bg-white">
        <div className="px-5 pt-5 pb-2">
          <h3 className="text-lg font-semibold text-stone-900">
            {method ? 'Edit payment method' : 'Add payment method'}
          </h3>
          <p className="mt-1 text-sm text-stone-500">
            Configure how students can pay. Enabled methods appear in the student
            payment modal and reception kiosk.
          </p>
        </div>

        <div className="space-y-4 px-5 py-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <label>
              <span className="mb-1 block text-sm font-medium text-stone-700">Type</span>
              <select
                value={values.type}
                onChange={(event) =>
                  setValues((prev) => ({
                    ...prev,
                    type: event.target.value,
                    details: {},
                  }))
                }
                className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-pink-500 focus:ring-pink-500"
              >
                {PAYMENT_METHOD_TYPES.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <label>
              <span className="mb-1 block text-sm font-medium text-stone-700">
                Display label
              </span>
              <input
                type="text"
                value={values.label}
                onChange={(event) =>
                  setValues((prev) => ({ ...prev, label: event.target.value }))
                }
                placeholder="e.g. Commercial Bank Colombo"
                className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-pink-500 focus:ring-pink-500"
              />
            </label>
          </div>

          <div>
            <p className="mb-2 text-sm font-medium text-stone-700">Available for</p>
            <div className="flex flex-wrap gap-3">
              {AUDIENCES.map((audience) => (
                <label
                  key={audience.value}
                  className="inline-flex items-center gap-2 rounded-md border border-stone-200 bg-white px-3 py-2 text-sm text-stone-700 shadow-sm"
                >
                  <input
                    type="checkbox"
                    checked={values.availableFor.includes(audience.value)}
                    onChange={() => toggleAudience(audience.value)}
                    className="h-4 w-4 rounded border-gray-300 text-pink-600 focus:ring-pink-500"
                  />
                  {audience.label}
                </label>
              ))}
            </div>
          </div>

          <div>
            <p className="mb-2 text-sm font-medium text-stone-700">Details</p>
            {renderTypeFields()}
          </div>

          <label className="block">
            <span className="mb-1 block text-sm font-medium text-stone-700">
              Instructions (optional)
            </span>
            <textarea
              value={values.instructions}
              onChange={(event) =>
                setValues((prev) => ({ ...prev, instructions: event.target.value }))
              }
              rows={3}
              placeholder="Shown to students after selecting this method."
              className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-pink-500 focus:ring-pink-500"
            />
          </label>

          <label className="inline-flex items-center gap-2">
            <input
              type="checkbox"
              checked={values.enabled}
              onChange={(event) =>
                setValues((prev) => ({ ...prev, enabled: event.target.checked }))
              }
              className="h-4 w-4 rounded border-gray-300 text-pink-600 focus:ring-pink-500"
            />
            <span className="text-sm text-stone-700">
              Enabled (visible to selected audiences)
            </span>
          </label>
        </div>

        {error ? (
          <p className="px-5 pb-2 text-sm text-red-600">{error}</p>
        ) : null}

        <div className="flex justify-end gap-2 border-t border-stone-200 bg-stone-50 px-5 py-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md px-4 py-2 text-sm font-medium text-stone-700 hover:bg-stone-100"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="inline-flex items-center gap-2 rounded-md bg-pink-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-pink-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {submitting ? 'Saving...' : method ? 'Save changes' : 'Create method'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default PaymentMethodForm;
